import { isTextKey } from '../i18n/i18n';
import type { TextKey } from '../i18n/i18n';
import { brainSettings } from '../rules/brain';
import type { BrainSettings } from '../rules/brain';
import { fightRules } from '../rules/rules';
import type { FightRules } from '../rules/rules';
import type { PunchStats } from '../rules/types';
import { punchStats } from '../rules/types';
import type { ShopChanges } from './shop';
import effectsJson from './effects.json';
import opponentJson from './opponent.json';
import punchesJson from './punches.json';
import rulesJson from './rules.json';

/** Sashko's numbers: every punch, the opponent, and the rules of a bout. Plain JSON, so they can be tuned without code. */
export interface Cards {
  readonly rules: FightRules;
  readonly playerHealth: number;
  readonly playerPunches: Readonly<Record<string, PunchStats>>;
  readonly opponentPunches: Readonly<Record<string, PunchStats>>;
  readonly opponent: OpponentCard;
  readonly effects: EffectCard;
}

/** How hard the show hits: hit-stops, camera shake, the knockout's slow motion, how many sparks fly. */
export type EffectCard = Readonly<typeof effectsJson>;

export interface OpponentCard {
  readonly nameKey: TextKey;
  readonly health: number;
  readonly brain: BrainSettings;
}

type RawPunch = Parameters<typeof punchStats>[0];
type RawPunches = Record<string, RawPunch>;

/** The numbers of a punch Sashko can change. */
export type PunchNumbers = Pick<PunchStats, 'damage' | 'windupSeconds' | 'activeSeconds' | 'recoverySeconds' | 'staminaCost'>;
type PunchChanges = Readonly<Record<string, Partial<PunchNumbers>>>;

/** Sashko's changes on top of the cards, kept in the browser; anything left out keeps the card's number. */
export interface CardChanges {
  readonly rules?: Partial<typeof rulesJson>;
  readonly opponent?: Partial<Omit<typeof opponentJson, 'nameKey' | 'favourites'>>;
  readonly effects?: Partial<EffectCard>;
  readonly punches?: { readonly player?: PunchChanges; readonly opponent?: PunchChanges };
  readonly shop?: ShopChanges;
}

function punches(raw: RawPunches, changes: PunchChanges = {}): Record<string, PunchStats> {
  return Object.fromEntries(Object.entries(raw).map(([id, stats]) => [id, punchStats({ ...stats, ...changes[id] })]));
}

export function loadCards(overrides: CardChanges = {}): Cards {
  const { playerHealth, ...rules } = { ...rulesJson, ...overrides.rules };
  const playerPunches = punches(punchesJson.player as RawPunches, overrides.punches?.player);
  const opponentPunches = punches(punchesJson.opponent as RawPunches, overrides.punches?.opponent);
  const opponent = { ...opponentJson, ...overrides.opponent };
  return {
    rules: fightRules(rules),
    playerHealth,
    playerPunches,
    opponentPunches,
    effects: effectCard({ ...effectsJson, ...overrides.effects }),
    opponent: {
      nameKey: textKey(opponent.nameKey),
      health: opponent.health,
      brain: brainSettings({
        pauseMinSeconds: opponent.pauseMinSeconds,
        pauseMaxSeconds: opponent.pauseMaxSeconds,
        blockChance: opponent.blockChance,
        reactionSeconds: opponent.reactionSeconds,
        counterChance: opponent.counterChance,
        staminaReserve: opponent.staminaReserve,
        blockPerFlurryPunch: opponent.blockPerFlurryPunch,
        comboChance: opponent.comboChance,
        comboMax: opponent.comboMax,
        favourites: opponent.favourites.map((f) => {
          const punch = opponentPunches[f.punch];
          if (!punch) throw new Error(`The opponent's card names a punch he does not have: ${f.punch}`);
          return { punch, weight: f.weight };
        }),
      }),
    },
  };
}

/** The same punch with the other hand: hooks, uppercuts and body blows are thrown with both. */
export function withHand(stats: PunchStats, hand: PunchStats['hand']): PunchStats {
  return { ...stats, hand };
}

function textKey(key: string): TextKey {
  if (!isTextKey(key)) throw new Error(`No text for the card's name key "${key}"`);
  return key;
}

function effectCard(card: EffectCard): EffectCard {
  for (const [name, value] of Object.entries(card)) {
    if (!Number.isFinite(value) || value < 0) throw new Error(`Effect card: ${name} must be a number of at least 0, not ${value}`);
  }
  return card;
}
